import TelegramActivationFeedback from "@/components/TelegramActivationFeedback";
import type { TelegramActivationPhase } from "@/utils/telegramActivation";
import {
  Box,
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export default function AuthPage({ phase, error, saleUnavailable, onActivate, onRetry, errorActionKey }: {
  phase: TelegramActivationPhase; error: string; saleUnavailable: boolean; errorActionKey?: string;
  onActivate: (code: string) => Promise<void>; onRetry: () => void;
}) {
  const { t } = useTranslation();
  const [activationCode, setActivationCode] = useState("");
  const isLoading = phase === "checking" || phase === "activating";
  const showCode = phase === "code" || phase === "activating";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!showCode || isLoading || !activationCode.trim()) return;
    await onActivate(activationCode.trim());
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2.5,
        bgcolor: "background.default",
        position: "relative",
      }}
    >
      {/* Background Gold Glow */}
      <Box
        sx={{
          position: "absolute",
          width: "300px",
          height: "300px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(196, 140, 58, 0.15) 0%, rgba(196, 140, 58, 0) 70%)",
          pointerEvents: "none",
        }}
      />

      <Card
        className="glass"
        sx={{
          width: "100%",
          maxWidth: "400px",
          borderRadius: "12px",
          border: "1px solid rgba(196, 140, 58, 0.25)",
          p: 1.5,
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            pt: 2,
            pb: 1,
          }}
        >
          <Box sx={{ width: 84, height: 84, mb: 1.5 }}>
            <img
              src="/e-theap-logo.png"
              alt="E-THEAP Logo"
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </Box>
          <Typography
            variant="h5"
            className="gold-gradient-text"
            sx={{ fontWeight: 700 }}
          >
            E-THEAP
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mt: 0.5, textAlign: "center" }}
          >
            {t(showCode ? "activationFlow.enterCode" : phase === "waiting" ? "activationFlow.waiting" : phase === "error" ? "activationFlow.failed" : "activationFlow.checking")}
          </Typography>
        </Box>

        <CardContent sx={{ pt: 2 }}>
          <TelegramActivationFeedback error={error} actionKey={errorActionKey} />
          {saleUnavailable && <Alert severity="info" sx={{ mt: 1.5 }}>{t("activationFlow.saleUnavailable")}</Alert>}
          {phase === "checking" && <Box role="status" aria-label={t("activationFlow.checking")} sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress /></Box>}
          {phase === "error" && <Button fullWidth variant="contained" sx={{ mt: 2 }} onClick={onRetry}>{t("retry")}</Button>}
          {showCode &&
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: error ? 2 : 0 }}
          >
            <TextField
              id="activation-code"
              label={t("activationFlow.code")}
              type="password"
              value={activationCode}
              onChange={(e) => setActivationCode(e.target.value)}
              autoComplete="off"
              required
              disabled={isLoading}
              fullWidth
              variant="outlined"
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              disabled={isLoading || !activationCode.trim()}
              sx={{
                py: 1.4,
                fontWeight: 700,
                fontSize: "1rem",
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t("activationFlow.activate")
              )}
            </Button>
          </Box>}
        </CardContent>
      </Card>
    </Box>
  );
}
